import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalDirective, ModalModule } from 'ngx-bootstrap/modal';
import { ApiNewsService } from '../../../../services/news/api/api-news.service';
import { AlertsService } from '../../../../services/alerts.service';
import { LanguagesService } from '../../../../services/languages/languages.service';
import { NewsTranslation } from '../../../../models/news/news-translation.model';
import { NewsPredefined } from '../../../../models/news/news-predefined.model';
import { NewsPeriodicityType } from '../../../../enums/news/news-periodicity-type.enum';

interface PeriodicityForm {
  enabled: boolean;
  type: NewsPeriodicityType;
  daysMask: number;
  dayStart: number;
  dayEnd: number;
  timeStart: string;
  timeEnd: string;
}

interface TranslationForm {
  languageId: number;
  languageIso: string;
  title: string;
  summary: string;
  content: string;
  linkButtonName: string;
  isCollapsed: boolean;
}

@Component({
  selector: 'app-news-add-modal',
  templateUrl: './news-add-modal.component.html',
  standalone: true,
  imports: [ModalModule, CommonModule, FormsModule]
})
export class NewsAddModalComponent {

  @ViewChild('modal') modal!: ModalDirective;

  protected readonly NewsPeriodicityType = NewsPeriodicityType;
  protected readonly periodicityTypes = [
    { value: NewsPeriodicityType.Daily, label: 'Daily' },
    { value: NewsPeriodicityType.Weekly, label: 'Weekly' },
    { value: NewsPeriodicityType.Monthly, label: 'Monthly' },
  ];
  protected readonly weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  isLoading: boolean = false;
  name: string = '';
  startDate: string = '';
  endDate: string = '';
  isActive: boolean = false;
  isRate: boolean = false;
  userCreatedAfterDate: string = '';
  userCreatedBeforeDate: string = '';
  translations: TranslationForm[] = [];
  periodicity: PeriodicityForm = this.defaultPeriodicity();

  linkDefault: string = '';
  linkIos: string = '';
  linkAndroid: string = '';
  linksCollapsed: boolean = true;

  selectedPredefinedId: number = 0;
  predefinedList: NewsPredefined[] = [];
  translationsFromPredefined: boolean = false;

  private callbackSaved: (() => void) | undefined;

  private defaultPeriodicity(): PeriodicityForm {
    return { enabled: false, type: NewsPeriodicityType.Daily, daysMask: 0, dayStart: 1, dayEnd: 31, timeStart: '00:00', timeEnd: '23:59' };
  }

  toggleWeekDay(bit: number): void {
    this.periodicity.daysMask ^= (1 << bit);
  }

  isWeekDayActive(bit: number): boolean {
    return (this.periodicity.daysMask & (1 << bit)) !== 0;
  }

  constructor(
    private apiNewsService: ApiNewsService,
    private alertsService: AlertsService,
    public languagesService: LanguagesService
  ) {}

  show(callbackSaved?: () => void): void {
    this.callbackSaved = callbackSaved;
    this.name = '';
    this.startDate = '';
    this.endDate = '';
    this.isActive = false;
    this.isRate = false;
    this.userCreatedAfterDate = '';
    this.userCreatedBeforeDate = '';
    this.selectedPredefinedId = 0;
    this.translationsFromPredefined = false;
    this.periodicity = this.defaultPeriodicity();
    this.isLoading = false;
    this.linkDefault = '';
    this.linkIos = '';
    this.linkAndroid = '';
    this.linksCollapsed = true;
    this.translations = this.languagesService.languages.map(l => ({
      languageId: l.languageId,
      languageIso: l.languageIso,
      title: '',
      summary: '',
      content: '',
      linkButtonName: '',
      isCollapsed: true
    }));
    this.apiNewsService.getNewsPredefined(1, 1000).then(
      response => { this.predefinedList = response.results; }
    );
    this.modal.show();
  }

  toggleTranslation(t: TranslationForm): void {
    t.isCollapsed = !t.isCollapsed;
  }

  toggleLinks(): void {
    this.linksCollapsed = !this.linksCollapsed;
  }

  onPredefinedChange(): void {
    if (this.selectedPredefinedId === 0) {
      this.translationsFromPredefined = false;
      return;
    }
    const predefined = this.predefinedList.find(p => p.newsPredefinedId === this.selectedPredefinedId);
    if (predefined) {
      this.translations = this.languagesService.languages.map(l => {
        const predTr = predefined.translations.find(t => t.languageId === l.languageId);
        return {
          languageId: l.languageId,
          languageIso: l.languageIso,
          title: predTr?.title ?? '',
          summary: predTr?.summary ?? '',
          content: predTr?.content ?? '',
          linkButtonName: predTr?.linkButtonName ?? '',
          isCollapsed: true
        };
      });
      this.translationsFromPredefined = true;
    }
  }

  onTranslationChanged(): void {
    if (this.translationsFromPredefined) {
      this.selectedPredefinedId = 0;
      this.translationsFromPredefined = false;
    }
  }

  save(): void {
    this.isLoading = true;
    const startTimestamp = this.startDate ? Math.floor(new Date(this.startDate + 'Z').getTime() / 1000) : 0;
    const endTimestamp = this.endDate ? Math.floor(new Date(this.endDate + 'Z').getTime() / 1000) : 0;
    const userCreatedAfterTimestamp = this.userCreatedAfterDate ? Math.floor(new Date(this.userCreatedAfterDate + 'Z').getTime() / 1000) : null;
    const userCreatedBeforeTimestamp = this.userCreatedBeforeDate ? Math.floor(new Date(this.userCreatedBeforeDate + 'Z').getTime() / 1000) : null;

    const newsPredefinedId = this.translationsFromPredefined ? this.selectedPredefinedId : 0;
    const translationsPayload: NewsTranslation[] = newsPredefinedId !== 0 ? [] : this.translations.map(t => {
      const tr = new NewsTranslation();
      tr.languageId = t.languageId;
      tr.title = t.title;
      tr.summary = t.summary;
      tr.content = t.content;
      tr.linkButtonName = t.linkButtonName;
      return tr;
    });

    const periodicity = this.periodicity.enabled ? {
      type: this.periodicity.type,
      days_mask: this.periodicity.type === NewsPeriodicityType.Weekly ? this.periodicity.daysMask : null,
      day_start: this.periodicity.type === NewsPeriodicityType.Monthly ? this.periodicity.dayStart : null,
      day_end: this.periodicity.type === NewsPeriodicityType.Monthly ? this.periodicity.dayEnd : null,
      time_start: this.periodicity.timeStart,
      time_end: this.periodicity.timeEnd,
    } : null;

    this.apiNewsService.addNews(this.name, startTimestamp, endTimestamp, this.isActive, this.isRate, translationsPayload, userCreatedAfterTimestamp, userCreatedBeforeTimestamp, newsPredefinedId, 0, periodicity, this.linkDefault, this.linkIos, this.linkAndroid).then(
      () => {
        this.isLoading = false;
        this.modal.hide();
        this.alertsService.displayAlertSuccess('News added !');
        if (this.callbackSaved) { this.callbackSaved(); }
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error adding news : ' + error);
      }
    );
  }
}
